import React, { useState } from 'react';
import { SignalIcon, Loader2Icon, AlertTriangleIcon, CheckCircle2Icon, XIcon, ActivityIcon } from 'lucide-react';
import { fetchTerrainProfile } from '../utils/elevation';
import { calculatePathLoss, type PathLossResult } from '../utils/pathLoss';
import { antennaGain, rxThreshold as computeThreshold, calculateLinkBudget, type LinkBudgetResult as LinkBudgetData } from '../utils/linkBudget';
import { DeviceParameters, DEFAULT_DEVICE_PARAMS, type DeviceParams } from './DeviceParameters';
import { LinkBudgetResult } from './LinkBudgetResult';
import { withGlobalLoading } from '../store/loadingStore';
interface LossCheckPanelProps {
  onClose: () => void;
  /** Prefill dari titik ruler yang sedang aktif, bila ada. */
  initialNE?: {
    lat: number;
    lng: number;
    id?: string;
  };
  initialFE?: {
    lat: number;
    lng: number;
    id?: string;
  };
}
const WIDTH = 1000;
const HEIGHT = 240;
const PAD_TOP = 18;
const PAD_BOTTOM = 26;
const STATUS_META = {
  clear: {
    color: '#34d399',
    label: 'Line of sight bersih',
    tone: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
  },
  marginal: {
    color: '#fbbf24',
    label: 'Fresnel terpotong sebagian',
    tone: 'bg-amber-500/10 border-amber-500/30 text-amber-200'
  },
  obstructed: {
    color: '#f87171',
    label: 'Lintasan terhalang',
    tone: 'bg-red-500/10 border-red-500/30 text-red-200'
  }
} as const;
export function LossCheckPanel({
  onClose,
  initialNE,
  initialFE
}: LossCheckPanelProps) {
  const [latNE, setLatNE] = useState(initialNE ? String(initialNE.lat) : '');
  const [lngNE, setLngNE] = useState(initialNE ? String(initialNE.lng) : '');
  const [latFE, setLatFE] = useState(initialFE ? String(initialFE.lat) : '');
  const [lngFE, setLngFE] = useState(initialFE ? String(initialFE.lng) : '');
  const [frequency, setFrequency] = useState('7.5');
  const [heightNE, setHeightNE] = useState('40');
  const [heightFE, setHeightFE] = useState('40');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PathLossResult | null>(null);
  const [deviceOpen, setDeviceOpen] = useState(true);
  const [device, setDevice] = useState<DeviceParams>(DEFAULT_DEVICE_PARAMS);
  const [budget, setBudget] = useState<LinkBudgetData | null>(null);
  const parse = (value: string) => {
    const num = parseFloat(value.replace(',', '.').trim());
    return Number.isFinite(num) ? num : null;
  };
  const updateDevice = <K extends keyof DeviceParams,>(key: K, value: DeviceParams[K]) => setDevice(prev => ({
    ...prev,
    [key]: value
  }));

  // Nilai auto mengikuti frekuensi, diameter antena, modulasi, dan bandwidth.
  const freqValue = parse(frequency) ?? 0;
  const autoGainNE = antennaGain(parse(device.diameterNE) ?? 0, freqValue);
  const autoGainFE = antennaGain(parse(device.diameterFE) ?? 0, freqValue);
  const autoThreshold = computeThreshold(device.modulation, parse(device.bandwidth) ?? 28);
  const handleCheck = async (event: React.FormEvent) => {
    event.preventDefault();
    const coords = {
      latNE: parse(latNE),
      lngNE: parse(lngNE),
      latFE: parse(latFE),
      lngFE: parse(lngFE)
    };
    const freq = parse(frequency);
    const hNE = parse(heightNE);
    const hFE = parse(heightFE);
    if (Object.values(coords).some(v => v === null)) {
      setError('Longitude dan latitude NE serta FE wajib diisi angka valid.');
      return;
    }
    if (!freq || freq <= 0) {
      setError('Frekuensi harus lebih besar dari 0 GHz.');
      return;
    }
    if (hNE === null || hFE === null || hNE < 0 || hFE < 0) {
      setError('Tinggi antena tidak valid.');
      return;
    }
    setError(null);
    setLoading(true);
    setResult(null);
    setBudget(null);
    try {
      const profile = await withGlobalLoading('loss-check', 'Menghitung link loss…', () => fetchTerrainProfile({
        lat: coords.latNE!,
        lng: coords.lngNE!
      }, {
        lat: coords.latFE!,
        lng: coords.lngFE!
      }));
      const loss = calculatePathLoss({
        samples: profile.samples,
        totalDistance: profile.totalDistance,
        frequencyGHz: freq,
        antennaHeightNE: hNE,
        antennaHeightFE: hFE
      });
      setResult(loss);

      // Link budget dihitung dari path loss + parameter perangkat.
      const gainNE = device.gainNEAuto ? antennaGain(parse(device.diameterNE) ?? 0, freq) : parse(device.gainNE) ?? 0;
      const gainFE = device.gainFEAuto ? antennaGain(parse(device.diameterFE) ?? 0, freq) : parse(device.gainFE) ?? 0;
      const threshold = device.rxThresholdAuto ? computeThreshold(device.modulation, parse(device.bandwidth) ?? 28) : parse(device.rxThreshold) ?? autoThreshold;
      setBudget(calculateLinkBudget({
        txPowerNE: parse(device.txPowerNE) ?? 0,
        txPowerFE: parse(device.txPowerFE) ?? 0,
        feederLossNE: parse(device.feederNE) ?? 0,
        feederLossFE: parse(device.feederFE) ?? 0,
        gainNE,
        gainFE,
        extraLoss: parse(device.extraLoss) ?? 0,
        rxThreshold: threshold,
        pathLoss: loss.totalLoss,
        fspl: loss.fspl,
        diffractionLoss: loss.diffractionLoss,
        frequencyGHz: freq,
        distanceKm: loss.distanceKm
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menghitung path loss.');
    } finally {
      setLoading(false);
    }
  };
  return <section className="glass-panel rounded-2xl p-4 md:p-5" data-id="element-388" data-mp="6ilrlhjhhxrl">
      <header className="flex items-center gap-3 mb-4" data-id="element-389" data-mp="1kk7ctabnsej">
        <div className="p-1.5 rounded-lg bg-cyan-500/10 border border-cyan-400/30 text-cyan-300" data-id="element-390" data-mp="1k22x9dia7vj">
          <SignalIcon size={15} data-id="element-391" data-mp="2rbgur7kzshey" />
        </div>
        <div className="flex flex-col leading-tight" data-id="element-392" data-mp="2mb2jzhs0atd8">
          <h3 className="text-xs font-semibold text-slate-200 uppercase tracking-[0.12em]" data-id="element-393" data-mp="2m980m7wdv9b9">
            Link Loss Check
          </h3>
          <span className="text-[11px] text-slate-500" data-id="element-394" data-mp="2sr2pmkvyziqk">
            Masukkan koordinat NE dan FE untuk menghitung path loss dan profil
            dataran
          </span>
        </div>
        <button onClick={onClose} aria-label="Tutup loss check" className="ml-auto p-1.5 rounded-lg text-slate-500 hover:text-red-300 hover:bg-red-500/15 transition-colors" data-id="element-395" data-mp="2ete91p8xqxrb">
          <XIcon size={14} data-id="element-396" data-mp="29s6yxjjblmhh" />
        </button>
      </header>

      <form onSubmit={handleCheck} className="space-y-3" data-id="element-397" data-mp="29trqd0ac8pb4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-398" data-mp="1w1bvwnvi5zlt">
          <Field label="Latitude NE" value={latNE} onChange={setLatNE} placeholder="-6.21462" data-id="element-399" data-mp="1vyz4zl7roewm" />
          <Field label="Longitude NE" value={lngNE} onChange={setLngNE} placeholder="106.84513" data-id="element-400" data-mp="1m3npv1sg55oc" />
          <Field label="Latitude FE" value={latFE} onChange={setLatFE} placeholder="-6.30112" data-id="element-401" data-mp="17fxg2xp06a1a" />
          <Field label="Longitude FE" value={lngFE} onChange={setLngFE} placeholder="106.91204" data-id="element-402" data-mp="12i47xz4535yd" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-end" data-id="element-403" data-mp="1ceh0gtny7drj">
          <Field label="Frekuensi (GHz)" value={frequency} onChange={setFrequency} placeholder="7.5" data-id="element-404" data-mp="1qerta28tj2fc" />
          <Field label="Tinggi antena NE (m)" value={heightNE} onChange={setHeightNE} placeholder="40" data-id="element-405" data-mp="1lf5g1sszpm6j" />
          <Field label="Tinggi antena FE (m)" value={heightFE} onChange={setHeightFE} placeholder="40" data-id="element-406" data-mp="1v587vpodku3a" />

          <button type="submit" disabled={loading} className="btn-primary flex items-center justify-center gap-2 text-xs text-white py-2.5 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-407" data-mp="292wr6t2snt68">
            {loading ? <>
                <Loader2Icon size={14} className="animate-spin" data-id="element-408" data-mp="3gjtagrikesjb" />
                Menghitung…
              </> : <>
                <ActivityIcon size={14} data-id="element-409" data-mp="3piwr7ou9evun" />
                Cek Loss
              </>}
          </button>
        </div>

        <DeviceParameters open={deviceOpen} onToggle={() => setDeviceOpen(v => !v)} params={device} onChange={updateDevice} autoGainNE={autoGainNE} autoGainFE={autoGainFE} autoThreshold={autoThreshold} data-id="element-410" data-mp="2stfcylh7o1hc" />
      </form>

      {budget && !loading && <LinkBudgetResult budget={budget} data-id="element-411" data-mp="36ku27a1poywu" />}

      {error && <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-xs" data-id="element-412" data-mp="2a0lbmucig7vl">
          <AlertTriangleIcon size={14} className="mt-0.5 flex-shrink-0" data-id="element-413" data-mp="2e157lulcl5g5" />
          <span data-id="element-414" data-mp="26ifivptvm98t">{error}</span>
        </div>}

      {result && !loading && <LossResult result={result} data-id="element-415" data-mp="21kdx2x52fvtn" />}
    </section>;
}
function Field({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return <label className="flex flex-col gap-1.5" data-id="element-416" data-mp="1sjfr49b2he8f">
      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]" data-id="element-417" data-mp="1nkkyygr7mdbn">
        {label}
      </span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} inputMode="decimal" className="input-dark w-full text-sm py-2 font-mono" data-id="element-418" data-mp="1itw5wwzvt8ae" />
    </label>;
}
function LossResult({
  result
}: {
  result: PathLossResult;
}) {
  const meta = STATUS_META[result.status];
  return <div className="mt-4 space-y-3" data-id="element-419" data-mp="14wfxjfy0z4g0">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-id="element-420" data-mp="zuqp8826t4vm">
        <Metric label="Jarak" value={`${result.distanceKm.toFixed(2)} km`} data-id="element-421" data-mp="zxmlzt2atnih" />
        <Metric label="Free space loss" value={`${result.fspl.toFixed(1)} dB`} data-id="element-422" data-mp="2sq5iltph4cs8" />
        <Metric label="Difraksi" value={`${result.diffractionLoss.toFixed(1)} dB`} tone={result.diffractionLoss > 0 ? 'warn' : undefined} data-id="element-423" data-mp="2opllgq4wh963" />
        <Metric label="Total path loss" value={`${result.totalLoss.toFixed(1)} dB`} tone="accent" data-id="element-424" data-mp="2xif3uwfn8oo6" />
      </div>

      <LossChart result={result} data-id="element-425" data-mp="29v704ixv5no9" />

      <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs border ${meta.tone}`} data-id="element-426" data-mp="25rsl9vlnvdce">
        {result.status === 'clear' ? <CheckCircle2Icon size={14} className="mt-0.5 flex-shrink-0" data-id="element-427" data-mp="2jslgyjnp8eh3" /> : <AlertTriangleIcon size={14} className="mt-0.5 flex-shrink-0" data-id="element-428" data-mp="2eqmct7k4xdj3" />}
        <span data-id="element-429" data-mp="3v5e1b9e5u0n4">
          <b data-id="element-430" data-mp="3v4w0dzgjyy8v">{meta.label}.</b> Fresnel zone pertama bebas{' '}
          <b data-id="element-431" data-mp="3q7vwnv76e7kx">{Math.round(result.worstFresnelClearance)}%</b> pada km{' '}
          <b data-id="element-432" data-mp="1bzl57cm9pib7">{result.worstPoint.distance.toFixed(1)}</b> (dataran{' '}
          {Math.round(result.worstPoint.terrain)} m, garis pandang{' '}
          {Math.round(result.worstPoint.lineOfSight)} m).
          {result.status !== 'clear' && ' Naikkan tinggi antena atau gunakan repeater agar clearance ≥ 60%.'}
        </span>
      </div>
    </div>;
}
function Metric({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone?: 'accent' | 'warn';
}) {
  return <div className="px-3 py-2 rounded-lg bg-slate-900/70 border border-slate-700/70" data-id="element-433" data-mp="1vpya8u3otxn4">
      <div className="text-[9px] text-slate-500 uppercase tracking-[0.12em] font-semibold" data-id="element-434" data-mp="1zoe8xv94sugd">
        {label}
      </div>
      <div className={`text-sm font-mono font-bold ${tone === 'accent' ? 'text-cyan-300' : tone === 'warn' ? 'text-amber-300' : 'text-slate-200'}`} data-id="element-435" data-mp="1qwvqtesmk4kn">
        {value}
      </div>
    </div>;
}
function LossChart({
  result
}: {
  result: PathLossResult;
}) {
  const {
    points
  } = result;
  const values = points.flatMap(p => [p.terrain, p.lineOfSight, p.fresnelBottom]);
  const top = Math.max(...values) * 1.08;
  const bottom = Math.min(...values, 0);
  const span = Math.max(top - bottom, 1);
  const total = result.distanceKm || 1;
  const x = (d: number) => d / total * WIDTH;
  const y = (v: number) => PAD_TOP + (1 - (v - bottom) / span) * (HEIGHT - PAD_TOP - PAD_BOTTOM);
  const line = (key: 'terrain' | 'lineOfSight' | 'fresnelBottom') => points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.distance)} ${y(p[key])}`).join(' ');
  const terrainArea = `${line('terrain')} L ${WIDTH} ${HEIGHT - PAD_BOTTOM} L 0 ${HEIGHT - PAD_BOTTOM} Z`;
  const fresnelArea = `${line('lineOfSight')} L ${points.slice().reverse().map(p => `${x(p.distance)} ${y(p.fresnelBottom)}`).join(' L ')} Z`;
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(ratio => ({
    ratio,
    value: Math.round(bottom + span * (1 - ratio)),
    y: PAD_TOP + ratio * (HEIGHT - PAD_TOP - PAD_BOTTOM)
  }));
  return <div data-id="element-436" data-mp="63hsey3ns7c7">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-[240px]" role="img" aria-label="Profil lintasan, garis pandang, dan zona Fresnel" preserveAspectRatio="none" data-id="element-437" data-mp="6380v62yts4e">
        {gridLines.map(g => <g key={g.ratio} data-id="element-438" data-mp="3ek2tzttyny5a">
            <line x1={0} x2={WIDTH} y1={g.y} y2={g.y} stroke="rgba(148,163,184,0.14)" data-id="element-439" data-mp="3ekuea8i3y9g9" />
            <text x={6} y={g.y - 4} fill="#64748b" fontSize={11} fontFamily="ui-monospace, monospace" data-id="element-440" data-mp="3oi8tk4saos6z">
              {g.value} m
            </text>
          </g>)}

        <path d={fresnelArea} fill="rgba(56,189,248,0.12)" data-id="element-441" data-mp="2vss1j88l7dba" />
        <path d={terrainArea} fill="rgba(16,185,129,0.16)" data-id="element-442" data-mp="30s4qbyj0qugi" />
        <path d={line('terrain')} fill="none" stroke="#34d399" strokeWidth={2} vectorEffect="non-scaling-stroke" data-id="element-443" data-mp="30qla27xd4jeq" />
        <path d={line('fresnelBottom')} fill="none" stroke="rgba(56,189,248,0.55)" strokeWidth={1.5} strokeDasharray="5 5" vectorEffect="non-scaling-stroke" data-id="element-444" data-mp="12c5kcsjwbn74" />
        <path d={line('lineOfSight')} fill="none" stroke={STATUS_META[result.status].color} strokeWidth={2} vectorEffect="non-scaling-stroke" data-id="element-445" data-mp="1b9g0ar7ns0id" />

        <line x1={x(result.worstPoint.distance)} y1={y(result.worstPoint.lineOfSight)} x2={x(result.worstPoint.distance)} y2={y(result.worstPoint.terrain)} stroke="#e2e8f0" strokeWidth={1.5} strokeDasharray="3 4" data-id="element-446" data-mp="1l3gerjotyi3r" />
        <circle cx={x(result.worstPoint.distance)} cy={y(result.worstPoint.terrain)} r={4} fill="#e2e8f0" data-id="element-447" data-mp="4p6mwfldqb86" />
      </svg>

      <div className="flex flex-wrap items-center gap-4 mt-2 pt-2.5 border-t border-slate-800/70" data-id="element-448" data-mp="2bcnspj1xdrrp">
        <Legend color="#34d399" label="Permukaan dataran + lengkung bumi" data-id="element-449" data-mp="2g94ggc57q449" />
        <Legend color={STATUS_META[result.status].color} label="Garis pandang antena" data-id="element-450" data-mp="22ju9xbn9sa93" />
        <Legend color="#38bdf8" label="Batas Fresnel zone 1" dashed data-id="element-451" data-mp="26jkxl6hi2a9e" />
        <Legend color="#e2e8f0" label="Titik kritis" data-id="element-452" data-mp="1sl4t7m740vuf" />
      </div>
    </div>;
}
function Legend({
  color,
  label,
  dashed
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) {
  return <div className="flex items-center gap-2" data-id="element-453" data-mp="1jkge0o5zcpfy">
      <span className="h-[2px] w-6 flex-shrink-0" style={dashed ? {
      backgroundImage: `repeating-linear-gradient(90deg, ${color} 0 5px, transparent 5px 9px)`
    } : {
      background: color
    }} data-id="element-454" data-mp="1jlrfuifv7n4f" />
      <span className="text-[10px] text-slate-400" data-id="element-455" data-mp="3hyhz3fuverxn">{label}</span>
    </div>;
}